import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from './inboxIcons';

// Settings → Problems: what the team reported as wrong in Support. Admins and Support managers move each one to
// "Working on it" or close it with a one-line reply the reporter sees. Order-data reports are worked by the Order Desk.
const TABS = [['open', 'Open'], ['working', 'Working on it'], ['closed', 'Closed']];
const when = (ms) => (ms ? new Date(ms).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '');
const WHERE = { message: 'Message', order: 'Order card', chat: 'Chat', brief: 'Brief', status: 'Status', cart: 'Cart' };

function Problem({ p, api, onDone, statusLabel }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const open = p.status === 'open' || p.status === 'working';
  const move = async (status) => {
    setBusy(status); setErr(null);
    try { await api(`/api/v1/support/problems/${p.id}`, 'POST', { status, note }); setNote(''); onDone(); } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };
  const s = p.snapshot || {};
  const sent = p.server?.message || s.message;
  return (
    <div className={`cx-prob${p.waiting && open ? ' waiting' : ''}`}>
      <div className="cx-prob-h">
        {p.waiting && open && <span className="cx-lv cx-lv-high">Customer waiting</span>}
        {p.deskReportId && <span className="cx-lv cx-lv-desk">Order Desk</span>}
        {p.type === 'no_answer' && <span className="cx-lv cx-lv-idea">Idea</span>}
        <b>{p.typeLabel}</b>
        <span className="cx-hint">{WHERE[p.where] || 'Chat'}{p.customerName ? ` · ${p.customerName}` : ''}{p.channel ? ` (${p.channel})` : ''} · by {p.by} · {when(p.createdAtMs)}</span>
        {!open && <span className="cx-chip cx-c-plain">{statusLabel[p.status] || p.status}</span>}
      </div>
      <div className="cx-prob-grid">
        {sent && <div className="cx-prob-box"><small>What was sent (saved when reported)</small>{sent.length > 400 ? `${sent.slice(0, 400)}…` : sent}{p.server?.automationLabel ? <em> · {p.server.automationLabel}</em> : ''}</div>}
        {p.where === 'order' && (s.stage || s.dates) && <div className="cx-prob-box"><small>Order card then{p.orderNumber ? ` · #${p.orderNumber}` : ''}</small>{[s.stage, s.dates, s.eta, s.payment].filter(Boolean).join(' · ')}{s.late ? ' · late' : ''}</div>}
        <div className="cx-prob-box"><small>{p.type === 'no_answer' ? 'What customers ask' : 'What it should be'}</small>{p.truth || '—'}</div>
      </div>
      <div className="cx-hint">Chat status then: {p.server?.chatStatus || '—'}{p.server?.task ? ` · task ${p.server.task}` : ''}</div>
      {p.closeNote && <div className="cx-prob-reply"><Icon name="chat" size={14} />{p.closeNote}{p.closedBy ? ` · ${p.closedBy}` : ''}</div>}
      {open && !p.deskReportId && (
        <div className="cx-prob-act">
          <input type="text" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reply to the reporter (needed for Can't fix / Not a problem)" aria-label="Reply to the reporter" />
          {p.status !== 'working' && <button className="cx-btn cx-btn-sm" onClick={() => move('working')} disabled={!!busy}>Working on it</button>}
          <button className="cx-btn cx-btn-primary cx-btn-sm" onClick={() => move('fixed')} disabled={!!busy}>Fixed</button>
          <button className="cx-btn cx-btn-sm" onClick={() => move('cant_fix')} disabled={!!busy}>Can't fix</button>
          <button className="cx-btn cx-btn-sm" onClick={() => move('not_problem')} disabled={!!busy}>Not a problem</button>
        </div>
      )}
      {p.deskForwardError && <div className="cx-hint">Could not send to the Order Desk ({p.deskForwardError}). Pass it on by hand, then close it here.</div>}
      {open && p.deskReportId && <div className="cx-hint">The Order Desk closes this one in their Reports tab; the reporter sees their reply here.</div>}
      {err && <div className="cx-err cx-err-box" role="alert">{err}</div>}
    </div>
  );
}

function ProblemsPage({ api }) {
  const [tab, setTab] = useState('open');
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => {
    setErr(null);
    api(`/api/v1/support/problems?status=${tab}`).then(setD).catch((e) => setErr(e.message));
  }, [api, tab]);
  useEffect(() => { load(); }, [load]);
  if (err) return <section className="cx-panel"><div className="cx-panel-b"><span className="cx-err">{err}</span></div></section>;
  if (!d) return <section className="cx-panel"><div className="cx-skel" style={{ height: 320, margin: 16 }} /></section>;
  const c = d.counts;
  return (
    <section className="cx-panel" aria-labelledby="cx-prob-h">
      <div className="cx-panel-h"><h2 id="cx-prob-h">Problems</h2><span>What the team flagged as wrong in Support</span></div>
      <div className="cx-panel-b">
        <div className="cx-metrics">
          <div className="cx-metric"><span>This week</span><b>{c.thisWeek}</b><small>{c.lastWeek} last week</small></div>
          <div className="cx-metric"><span>Customer waiting</span><b style={{ color: c.waitingOpen ? '#a3302a' : undefined }}>{c.waitingOpen}</b><small>open now</small></div>
          <div className="cx-metric"><span>Top type this week</span><b style={{ fontSize: 17 }}>{c.topType ? c.topType.label : '—'}</b><small>{c.topType ? `${c.topType.value} of ${c.thisWeek}` : 'nothing reported'}</small></div>
          <div className="cx-metric"><span>Time to close</span><b>{c.medianDaysToClose == null ? '—' : `${c.medianDaysToClose} d`}</b><small>median</small></div>
        </div>
        <div className="cx-seg" role="group" aria-label="Show" style={{ margin: '4px 0 14px', maxWidth: 420 }}>
          {TABS.map(([id, l]) => <button key={id} aria-pressed={tab === id} onClick={() => setTab(id)}>{l} {id === 'open' ? c.open : id === 'working' ? c.working : c.closed}</button>)}
        </div>
        {d.problems.length ? <div className="cx-prob-list">{d.problems.map((p) => <Problem key={p.id} p={p} api={api} onDone={load} statusLabel={d.statusLabel} />)}</div>
          : <p className="cx-hint">{tab === 'closed' ? 'Nothing closed yet.' : 'Nothing here. Reports appear when someone uses "Report a problem" in a chat.'}</p>}
      </div>
    </section>
  );
}

export default ProblemsPage;
